import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminApi } from '../../lib/api';
import { useToast } from '../../components/ToastProvider';

export function AdminDashboard() {
  const { isAuthenticated, logout, getAuthHeader } = useAdminAuth();
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [viewingUser, setViewingUser] = useState<any>(null);
  const [deletingUser, setDeletingUser] = useState<any>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const { push } = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  async function loadData() {
    try {
      setLoading(true);
      const authHeader = getAuthHeader();
      const [statsData, usersData] = await Promise.all([
        adminApi.getStats(authHeader),
        adminApi.listUsers(authHeader)
      ]);
      setStats(statsData);
      setUsers(usersData.users);
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }


  async function handleDeleteUser() {
    if (!deletingUser) return;

    if (deleteConfirmEmail !== deletingUser.email) {
      push('Email de confirmação não corresponde', 'error');
      return;
    }

    try {
      const authHeader = getAuthHeader();
      await adminApi.deleteUser(deletingUser._id, authHeader);
      push('Usuário excluído com sucesso', 'success');
      setDeletingUser(null);
      setDeleteConfirmEmail('');
      loadData();
    } catch (e: any) {
      push(e.message, 'error');
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--cg-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-[var(--cg-surface)]/80 border-b" style={{ borderColor: 'var(--cg-border)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                  <i className="fa-solid fa-shield-halved text-white"></i>
                </div>
                <div>
                  <h1 className="font-bold text-lg">Admin Dashboard</h1>
                  <p className="text-xs opacity-60">CopyGenius</p>
                </div>
              </div>

              <Link
                to="/admin/enterprise"
                className="px-4 py-2 rounded-lg hover:bg-[var(--cg-surface-alt)] text-sm font-medium transition-colors flex items-center gap-2"
              >
                <i className="fa-solid fa-crown"></i>
                Enterprise
              </Link>
            </div>

            <button
              onClick={logout}
              className="btn btn-danger h-9 px-4 text-xs"
            >
              <i className="fa-solid fa-right-from-bracket"></i> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {loading ? (
          <div className="text-center py-20">
            <i className="fa-solid fa-spinner fa-spin text-4xl opacity-50"></i>
            <p className="mt-4 opacity-60">Carregando...</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid gap-6 md:grid-cols-4 mb-8">
                <StatCard
                  icon="fa-users"
                  label="Total Usuários"
                  value={stats.totalUsers}
                  color="blue"
                />
                <StatCard
                  icon="fa-crown"
                  label="Plano Pro"
                  value={stats.proUsers}
                  color="purple"
                />
                <StatCard
                  icon="fa-check-circle"
                  label="Assinaturas Ativas"
                  value={stats.activeSubscriptions}
                  color="green"
                />
                <StatCard
                  icon="fa-flask"
                  label="Trial"
                  value={stats.trialUsers}
                  color="amber"
                />
              </div>
            )}

            {/* Users Table */}
            <div className="panel p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold heading">Gerenciar Usuários</h2>
                <button
                  onClick={loadData}
                  className="btn btn-ghost h-8 px-3 text-xs"
                >
                  <i className="fa-solid fa-rotate"></i> Atualizar
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--cg-border)' }}>
                      <th className="text-left py-3 px-2 font-semibold">Nome</th>
                      <th className="text-left py-3 px-2 font-semibold">Email</th>
                      <th className="text-center py-3 px-2 font-semibold">Plano</th>
                      <th className="text-center py-3 px-2 font-semibold">Textos</th>
                      <th className="text-center py-3 px-2 font-semibold">Chats</th>
                      <th className="text-center py-3 px-2 font-semibold">Imagens</th>
                      <th className="text-center py-3 px-2 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => {
                      const subscription = user.subscription || {};
                      const limits = subscription.limits || {};
                      const usage = subscription.usage || {};
                      const plan = subscription.plan || user.plan || 'free';

                      return (
                        <tr
                          key={user._id}
                          className="border-b hover:bg-[var(--cg-surface-alt)] transition-colors"
                          style={{ borderColor: 'var(--cg-border)' }}
                        >
                          <td className="py-3 px-2">{user.name}</td>
                          <td className="py-3 px-2 text-xs opacity-70">{user.email}</td>
                          <td className="text-center py-3 px-2">
                            <span className={`px-2 py-1 rounded text-[10px] font-medium ${
                              plan === 'enterprise' ? 'bg-yellow-500/20 text-yellow-300' :
                              plan === 'business' ? 'bg-blue-500/20 text-blue-300' :
                              plan === 'pro' ? 'bg-purple-500/20 text-purple-300' :
                              'bg-gray-500/20 text-gray-300'
                            }`}>
                              {plan.toUpperCase()}
                            </span>
                          </td>
                          <td className="text-center py-3 px-2">
                            <div className="text-[10px]">
                              <span className="font-semibold">{usage.textsGenerated || 0}</span>
                              <span className="opacity-50"> / </span>
                              <span className="opacity-70">{limits.textsPerMonth === 'unlimited' ? '∞' : limits.textsPerMonth || 0}</span>
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">
                            <div className="text-[10px]">
                              <span className="font-semibold">{usage.chatsCreated || 0}</span>
                              <span className="opacity-50"> / </span>
                              <span className="opacity-70">{limits.chatsPerMonth === 'unlimited' ? '∞' : limits.chatsPerMonth || 0}</span>
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">
                            <div className="text-[10px]">
                              <span className="font-semibold">{usage.imagesGenerated || 0}</span>
                              <span className="opacity-50"> / </span>
                              <span className="opacity-70">{limits.imagesPerMonth || 0}</span>
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => setViewingUser(user)}
                                className="btn btn-ghost h-7 px-2 text-xs"
                                title="Ver detalhes"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>
                              <button
                                onClick={() => setEditingUser(user)}
                                className="btn btn-ghost h-7 px-2 text-xs"
                                title="Editar"
                              >
                                <i className="fa-solid fa-pen"></i>
                              </button>
                              <button
                                onClick={() => setDeletingUser(user)}
                                className="btn btn-ghost h-7 px-2 text-xs text-red-500 hover:bg-red-500/10"
                                title="Excluir"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Edit Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* View Details Modal */}
      {viewingUser && (
        <ViewUserModal
          user={viewingUser}
          onClose={() => setViewingUser(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          confirmEmail={deleteConfirmEmail}
          onConfirmEmailChange={setDeleteConfirmEmail}
          onClose={() => {
            setDeletingUser(null);
            setDeleteConfirmEmail('');
          }}
          onDelete={handleDeleteUser}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    amber: 'from-amber-500 to-amber-600'
  };

  return (
    <div className="panel p-5 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br opacity-10 rounded-full blur-2xl" style={{ background: `var(--cg-accent)` }}></div>
      <div className="relative">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center mb-3`}>
          <i className={`fa-solid ${icon} text-white text-lg`}></i>
        </div>
        <div className="text-3xl font-bold heading mb-1">{value}</div>
        <div className="text-xs opacity-70">{label}</div>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose }: any) {
  const subscription = user.subscription || {};
  const limits = subscription.limits || {};
  const usage = subscription.usage || {};

  const [textsLimit, setTextsLimit] = useState(limits.textsPerMonth === 'unlimited' ? -1 : (limits.textsPerMonth || 50));
  const [chatsLimit, setChatsLimit] = useState(limits.chatsPerMonth === 'unlimited' ? -1 : (limits.chatsPerMonth || 10));
  const [imagesLimit, setImagesLimit] = useState(limits.imagesPerMonth || 20);
  const [metaAccountsLimit, setMetaAccountsLimit] = useState(limits.metaAccounts === 'unlimited' ? -1 : (limits.metaAccounts || 1));

  const [textsUsage, setTextsUsage] = useState(usage.textsGenerated || 0);
  const [chatsUsage, setChatsUsage] = useState(usage.chatsCreated || 0);
  const [imagesUsage, setImagesUsage] = useState(usage.imagesGenerated || 0);

  const [saving, setSaving] = useState(false);
  const { push } = useToast();
  const { getAuthHeader } = useAdminAuth();

  async function handleSaveLimits() {
    try {
      setSaving(true);
      const authHeader = getAuthHeader();

      const customLimits = {
        textsPerMonth: textsLimit === -1 ? 'unlimited' : textsLimit,
        chatsPerMonth: chatsLimit === -1 ? 'unlimited' : chatsLimit,
        imagesPerMonth: imagesLimit,
        metaAccounts: metaAccountsLimit === -1 ? 'unlimited' : metaAccountsLimit
      };

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://backend.copygenius.gustavohenrique.dev'}/api/admin/users/${user._id}/custom-limits`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ limits: customLimits, customNote: 'Ajustado pelo admin' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar limites');
      }

      push('Limites atualizados com sucesso!', 'success');

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleResetUsage() {
    try {
      setSaving(true);
      const authHeader = getAuthHeader();

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://backend.copygenius.gustavohenrique.dev'}/api/admin/users/${user._id}/reset-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao resetar uso');
      }

      push('Uso resetado com sucesso!', 'success');

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="panel p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold heading">Gerenciar Limites e Uso</h3>
          <button onClick={onClose} className="btn btn-ghost h-8 w-8 p-0" disabled={saving}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="space-y-5">
          {/* Info do usuário */}
          <div className="panel-alt p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="opacity-60">Nome</span>
              <span className="font-medium">{user.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="opacity-60">Email</span>
              <span className="font-medium">{user.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="opacity-60">Plano</span>
              <span className="font-medium uppercase">{subscription.plan || 'FREE'}</span>
            </div>
          </div>

          {/* Limites Mensais */}
          <div>
            <h4 className="font-semibold text-sm mb-3 pb-2 border-b" style={{borderColor: 'var(--cg-border)'}}>
              <i className="fa-solid fa-sliders text-blue-400 mr-2"></i>
              Limites Mensais
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-2">
                  <i className="fa-solid fa-file-text text-green-400 mr-1"></i> Textos por Mês
                </label>
                <input
                  type="number"
                  className="input w-full text-sm"
                  value={textsLimit}
                  onChange={e => setTextsLimit(Number(e.target.value))}
                  disabled={saving}
                />
                <p className="text-xs opacity-60 mt-1">-1 = ilimitado</p>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2">
                  <i className="fa-solid fa-comments text-purple-400 mr-1"></i> Chats por Mês
                </label>
                <input
                  type="number"
                  className="input w-full text-sm"
                  value={chatsLimit}
                  onChange={e => setChatsLimit(Number(e.target.value))}
                  disabled={saving}
                />
                <p className="text-xs opacity-60 mt-1">-1 = ilimitado</p>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2">
                  <i className="fa-solid fa-image text-amber-400 mr-1"></i> Imagens por Mês
                </label>
                <input
                  type="number"
                  className="input w-full text-sm"
                  value={imagesLimit}
                  onChange={e => setImagesLimit(Number(e.target.value))}
                  min={0}
                  disabled={saving}
                />
                <p className="text-xs opacity-60 mt-1">Apenas números positivos</p>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2">
                  <i className="fa-solid fa-link text-blue-400 mr-1"></i> Contas Meta
                </label>
                <input
                  type="number"
                  className="input w-full text-sm"
                  value={metaAccountsLimit}
                  onChange={e => setMetaAccountsLimit(Number(e.target.value))}
                  disabled={saving}
                />
                <p className="text-xs opacity-60 mt-1">-1 = ilimitado</p>
              </div>
            </div>
          </div>

          {/* Uso Atual */}
          <div>
            <div className="flex items-center justify-between mb-3 pb-2 border-b" style={{borderColor: 'var(--cg-border)'}}>
              <h4 className="font-semibold text-sm">
                <i className="fa-solid fa-chart-line text-amber-400 mr-2"></i>
                Uso Atual (Ciclo)
              </h4>
              <button
                onClick={handleResetUsage}
                className="btn btn-ghost h-8 px-3 text-xs"
                disabled={saving}
              >
                <i className="fa-solid fa-rotate-right"></i> Resetar Uso
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="panel-alt p-3 text-center">
                <div className="text-2xl font-bold text-green-400">{usage.textsGenerated || 0}</div>
                <div className="text-xs opacity-60 mt-1">Textos Gerados</div>
              </div>

              <div className="panel-alt p-3 text-center">
                <div className="text-2xl font-bold text-purple-400">{usage.chatsCreated || 0}</div>
                <div className="text-xs opacity-60 mt-1">Chats Criados</div>
              </div>

              <div className="panel-alt p-3 text-center">
                <div className="text-2xl font-bold text-amber-400">{usage.imagesGenerated || 0}</div>
                <div className="text-xs opacity-60 mt-1">Imagens Geradas</div>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-3 border-t" style={{borderColor: 'var(--cg-border)'}}>
            <button
              onClick={onClose}
              className="btn btn-outline h-11 px-5 text-sm flex-1"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveLimits}
              className="btn btn-primary h-11 px-5 text-sm flex-1"
              disabled={saving}
            >
              {saving ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Salvando...</>
              ) : (
                <><i className="fa-solid fa-check"></i> Salvar Limites</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewUserModal({ user, onClose }: any) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="panel p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold heading">Detalhes do Usuário</h3>
          <button onClick={onClose} className="btn btn-ghost h-8 w-8 p-0">
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="space-y-6">
          {/* Informações Pessoais */}
          <div>
            <h4 className="font-semibold text-sm mb-3 pb-2 border-b" style={{borderColor: 'var(--cg-border)'}}>
              <i className="fa-solid fa-user text-brand-400 mr-2"></i>
              Informações Pessoais
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="Nome" value={user.name} />
              <InfoItem label="Email" value={user.email} />
              <InfoItem label="CPF" value={user.cpf || 'Não informado'} />
              <InfoItem label="Telefone" value={user.phone || 'Não informado'} />
              <InfoItem label="Data de Nascimento" value={user.birthDate ? new Date(user.birthDate).toLocaleDateString('pt-BR') : 'Não informado'} />
              <InfoItem label="Criado em" value={new Date(user.createdAt).toLocaleString('pt-BR')} />
            </div>
          </div>

          {/* Assinatura */}
          <div>
            <h4 className="font-semibold text-sm mb-3 pb-2 border-b" style={{borderColor: 'var(--cg-border)'}}>
              <i className="fa-solid fa-credit-card text-purple-400 mr-2"></i>
              Assinatura
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="Plano" value={user.plan} />
              <InfoItem label="Créditos" value={user.credits} color="text-blue-400" />
              <InfoItem
                label="Status"
                value={user.subscriptionStatus}
                color={
                  user.subscriptionStatus === 'Ativa' ? 'text-green-400' :
                  user.subscriptionStatus === 'Inadimplente' ? 'text-amber-400' :
                  'text-red-400'
                }
              />
              <InfoItem label="Ciclo" value={user.billingCycle || 'N/A'} />
              {user.currentCycleStart && (
                <InfoItem label="Início do Ciclo" value={new Date(user.currentCycleStart).toLocaleDateString('pt-BR')} />
              )}
              {user.currentCycleEnd && (
                <InfoItem label="Fim do Ciclo" value={new Date(user.currentCycleEnd).toLocaleDateString('pt-BR')} />
              )}
              {user.trialEndsAt && (
                <InfoItem label="Trial até" value={new Date(user.trialEndsAt).toLocaleDateString('pt-BR')} />
              )}
            </div>
          </div>

          {/* Integração Asaas */}
          {(user.asaasCustomerId || user.subscriptionId) && (
            <div>
              <h4 className="font-semibold text-sm mb-3 pb-2 border-b" style={{borderColor: 'var(--cg-border)'}}>
                <i className="fa-solid fa-link text-green-400 mr-2"></i>
                Integração Asaas
              </h4>
              <div className="grid grid-cols-1 gap-3">
                {user.asaasCustomerId && (
                  <div className="panel-alt p-3">
                    <div className="text-xs opacity-60 mb-1">Customer ID</div>
                    <div className="font-mono text-xs break-all">{user.asaasCustomerId}</div>
                  </div>
                )}
                {user.subscriptionId && (
                  <div className="panel-alt p-3">
                    <div className="text-xs opacity-60 mb-1">Subscription ID</div>
                    <div className="font-mono text-xs break-all">{user.subscriptionId}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="btn btn-outline w-full h-11 mt-6"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

function InfoItem({ label, value, color }: { label: string; value: any; color?: string }) {
  return (
    <div>
      <div className="text-xs opacity-60 mb-1">{label}</div>
      <div className={`text-sm font-medium ${color || ''}`}>{value}</div>
    </div>
  );
}

function DeleteUserModal({ user, confirmEmail, onConfirmEmailChange, onClose, onDelete }: any) {
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    await onDelete();
    setDeleting(false);
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="panel p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-red-500">
            <i className="fa-solid fa-triangle-exclamation mr-2"></i>
            Confirmar Exclusão
          </h3>
          <button onClick={onClose} className="btn btn-ghost h-8 w-8 p-0" disabled={deleting}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="space-y-4">
          <div className="panel-alt p-4 border-l-4 border-red-500">
            <p className="text-sm mb-2">Você está prestes a excluir:</p>
            <p className="font-semibold">{user.name}</p>
            <p className="text-sm opacity-70">{user.email}</p>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-sm text-amber-300 font-medium mb-2">
              <i className="fa-solid fa-exclamation-triangle mr-2"></i>
              Esta ação é IRREVERSÍVEL
            </p>
            <ul className="list-disc ml-5 text-xs text-amber-200/80 space-y-1">
              <li>Cancelará assinatura ativa no Asaas</li>
              <li>Excluirá todas as conversas do usuário</li>
              <li>Excluirá todas as gerações</li>
              <li>Excluirá todos os eventos de billing</li>
              <li>Removerá o usuário permanentemente</li>
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Digite o email do usuário para confirmar:
            </label>
            <input
              type="email"
              value={confirmEmail}
              onChange={e => onConfirmEmailChange(e.target.value)}
              placeholder={user.email}
              className="input w-full"
              disabled={deleting}
            />
            <p className="text-xs opacity-60 mt-1">Email: {user.email}</p>
          </div>

          <div className="flex gap-3 pt-3 border-t" style={{borderColor: 'var(--cg-border)'}}>
            <button
              onClick={onClose}
              className="btn btn-outline h-11 px-5 text-sm flex-1"
              disabled={deleting}
            >
              Cancelar
            </button>
            <button
              onClick={handleDelete}
              className="btn btn-danger h-11 px-5 text-sm flex-1"
              disabled={deleting || confirmEmail !== user.email}
            >
              {deleting ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Excluindo...</>
              ) : (
                <><i className="fa-solid fa-trash"></i> Excluir Permanentemente</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
